import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useOrganizerAuth } from '../contexts/OrganizerAuthContext';
import { useEvent } from '../contexts/EventContext';
import {
  Users, Calendar, MessageSquare, BarChart2, LogOut,
  Search, CheckCircle, XCircle, Clock, Table2, RefreshCw,
  ChevronDown, ChevronUp, ChevronsUpDown, Building2, UserCheck, AlertTriangle, KeyRound,
  X, Mail, Phone, MapPin, ExternalLink, Download, LayoutGrid,
  Link2, UserPlus, ThumbsDown, Target, Pencil, Trash2, Save
} from 'lucide-react';
import ContentManagementTab from './ContentManagementTab';
import ORG_I18N, { SUPPORTED_LANGS, LANG_LABELS, getOrgLang } from '../i18n/organizerDashboard';

const API = '/api';

// ─── Sort helpers ─────────────────────────────────────────────────────────────

const useSort = (initial = null) => {
  const [sortKey, setSortKey] = useState(initial);
  const [sortDir, setSortDir] = useState('asc');
  const toggle = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };
  return { sortKey, sortDir, toggle };
};

const applySortStr = (data, key, dir, getter) => {
  if (!key) return data;
  return [...data].sort((a, b) => {
    const va = ((getter ? getter(a, key) : a[key]) ?? '').toString().toLowerCase();
    const vb = ((getter ? getter(b, key) : b[key]) ?? '').toString().toLowerCase();
    return dir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
  });
};

const SortIcon = ({ col, sortKey, sortDir }) => {
  if (sortKey !== col) return <ChevronsUpDown size={12} className="ml-1 opacity-30 flex-shrink-0" />;
  return sortDir === 'asc'
    ? <ChevronUp   size={12} className="ml-1 text-blue-500 flex-shrink-0" />
    : <ChevronDown size={12} className="ml-1 text-blue-500 flex-shrink-0" />;
};

const Th = ({ col, label, sortKey, sortDir, onSort, className = '' }) => (
  <th
    onClick={() => onSort(col)}
    className={`cursor-pointer select-none group hover:bg-gray-100 transition ${className}`}
  >
    <div className="flex items-center gap-0.5">
      {label}
      <SortIcon col={col} sortKey={sortKey} sortDir={sortDir} />
    </div>
  </th>
);

// ─── Helpers ────────────────────────────────────────────────────────────────

const CHART_COLORS = [
  '#3B82F6','#8B5CF6','#10B981','#F59E0B','#EF4444',
  '#06B6D4','#F97316','#EC4899','#84CC16','#6366F1','#14B8A6','#A855F7',
];

const statusColor = {
  Confirmed: 'bg-green-100 text-green-700',
  Pending:   'bg-yellow-100 text-yellow-700',
  Accepted:  'bg-blue-100 text-blue-700',
  Rejected:  'bg-red-100 text-red-700',
  Declined:  'bg-red-100 text-red-700',
  Cancelled: 'bg-gray-100 text-gray-500',
};

const StatCard = ({ icon: Icon, label, value, color = 'blue', sub }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-start gap-4">
    <div className={`w-12 h-12 rounded-xl bg-${color}-50 flex items-center justify-center flex-shrink-0`}>
      <Icon className={`text-${color}-600`} size={22} />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

// ─── Overview sub-components ─────────────────────────────────────────────────

const DonutChart = ({ segments, total, size = 156 }) => {
  const cx = size / 2, cy = size / 2;
  const r = size * 0.40, ir = size * 0.24;
  let angle = -Math.PI / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="flex-shrink-0">
      {segments.map((s, i) => {
        const sweep = (s.value / total) * 2 * Math.PI;
        if (sweep < 0.001) return null;
        const end = angle + sweep;
        const large = sweep > Math.PI ? 1 : 0;
        const x1 = cx + r * Math.cos(angle),  y1 = cy + r * Math.sin(angle);
        const x2 = cx + r * Math.cos(end),    y2 = cy + r * Math.sin(end);
        const ix1 = cx + ir * Math.cos(end),  iy1 = cy + ir * Math.sin(end);
        const ix2 = cx + ir * Math.cos(angle),iy2 = cy + ir * Math.sin(angle);
        const d = `M${x1},${y1} A${r},${r},0,${large},1,${x2},${y2} L${ix1},${iy1} A${ir},${ir},0,${large},0,${ix2},${iy2}Z`;
        angle = end;
        return <path key={i} d={d} fill={s.color} />;
      })}
      <text x={cx} y={cy - 5} textAnchor="middle" fontSize="22" fontWeight="bold" fill="#111827">{total}</text>
      <text x={cx} y={cy + 13} textAnchor="middle" fontSize="10" fill="#9ca3af">co.</text>
    </svg>
  );
};

const CompanyTypesChart = ({ data, t }) => {
  const filtered = (data || []).filter(d => d.count > 0);
  const total = filtered.reduce((s, d) => s + d.count, 0);
  if (!filtered.length || !total)
    return <p className="text-gray-400 text-sm text-center py-8">{t.chart_no_data}</p>;
  const segments = filtered.map((d, i) => ({ ...d, value: d.count, color: CHART_COLORS[i % CHART_COLORS.length] }));
  return (
    <div className="flex items-start gap-5">
      <DonutChart segments={segments} total={total} />
      <div className="flex-1 space-y-1.5 min-w-0 pt-1">
        {segments.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-xs text-gray-600 flex-1 truncate">{s.type}</span>
            <span className="text-xs font-semibold text-gray-800">{s.count}</span>
            <span className="text-xs text-gray-400 w-9 text-right">{Math.round(s.count / total * 100)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const RegistrationFunnel = ({ stats, t }) => {
  const total = stats.totalRegistrations;
  if (!total) return <p className="text-gray-400 text-sm text-center py-8">{t.chart_no_data}</p>;
  const items = [
    { key: 'new',      label: t.pv_status_new,      value: stats.newRegistrations,      bar: '#9ca3af' },
    { key: 'pending',  label: t.pv_status_pending,   value: stats.pendingRegistrations,  bar: '#f97316' },
    { key: 'accepted', label: t.pv_status_accepted,  value: stats.acceptedRegistrations, bar: '#22c55e' },
    { key: 'declined', label: t.pv_status_declined,  value: stats.declinedRegistrations, bar: '#ef4444' },
  ];
  return (
    <div>
      {/* Stacked bar */}
      <div className="flex h-3 rounded-full overflow-hidden gap-px mb-5">
        {items.filter(i => i.value > 0).map(item => (
          <div key={item.key} className="transition-all" style={{ width: `${item.value / total * 100}%`, backgroundColor: item.bar }} />
        ))}
      </div>
      <div className="space-y-2">
        {items.map(item => (
          <div key={item.key} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.bar }} />
              <span className="text-sm text-gray-600">{item.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm font-semibold text-gray-800">{item.value}</span>
              <span className="text-xs text-gray-400 w-9 text-right">{Math.round(item.value / total * 100)}%</span>
            </div>
          </div>
        ))}
        <div className="border-t border-gray-100 pt-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-600">Total</span>
          <span className="text-sm font-bold text-gray-900">{total}</span>
        </div>
      </div>
    </div>
  );
};

const TopCountries = ({ data, t }) => {
  if (!data?.length) return <p className="text-gray-400 text-sm text-center py-4">{t.chart_no_data}</p>;
  const max = data[0]?.[1] || 1;
  return (
    <div className="space-y-3">
      {data.map(([country, count], i) => (
        <div key={country} className="flex items-center gap-3">
          <span className="text-xs text-gray-400 w-4 text-center">{i + 1}</span>
          <span className="text-sm text-gray-700 flex-1 truncate">{country}</span>
          <div className="w-20 bg-gray-100 rounded-full h-1.5">
            <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${count / max * 100}%` }} />
          </div>
          <span className="text-sm font-semibold text-gray-800 w-5 text-right">{count}</span>
        </div>
      ))}
    </div>
  );
};

const ProgressCard = ({ label, rate, sub, barColor = '#2563eb' }) => (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-semibold text-gray-700 text-sm">{label}</h3>
      <span className="text-xl font-bold text-gray-900">{rate}%</span>
    </div>
    <div className="bg-gray-100 rounded-full h-2.5 overflow-hidden">
      <div className="h-2.5 rounded-full transition-all duration-700" style={{ width: `${rate}%`, backgroundColor: barColor }} />
    </div>
    <p className="text-xs text-gray-400 mt-2">{sub}</p>
  </div>
);

// ─── Tab: Overview ───────────────────────────────────────────────────────────

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 mb-4 mt-8 first:mt-0">
    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center">
      <Icon className="text-gray-600" size={16} />
    </div>
    <h3 className="text-base font-bold text-gray-800">{children}</h3>
    <div className="flex-1 border-t border-gray-200 ml-2" />
  </div>
);

const HorizontalBarList = ({ data, t }) => {
  if (!data?.length) return <p className="text-gray-400 text-sm text-center py-4">{t.chart_no_data}</p>;
  const max = data[0]?.[1] || 1;
  return (
    <div className="space-y-2.5">
      {data.map(([label, count]) => (
        <div key={label} className="flex items-center gap-3">
          <span className="text-sm text-gray-700 flex-1 truncate">{label}</span>
          <div className="w-24 bg-gray-100 rounded-full h-1.5">
            <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${count / max * 100}%` }} />
          </div>
          <span className="text-sm font-semibold text-gray-800 w-6 text-right">{count}</span>
        </div>
      ))}
    </div>
  );
};

const OverviewTab = ({ stats, companyTypes, t }) => (
  <div>
    <h2 className="text-lg font-bold text-gray-800 mb-2">{t.overview_title}</h2>

    {/* ═══════ SECTION PARTICIPANTS ═══════ */}
    <SectionTitle icon={Users}>{t.ov_section_participants}</SectionTitle>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      <StatCard icon={Users}     label={t.stat_participants} value={stats.totalParticipants} color="blue" />
      <StatCard icon={UserCheck} label={t.stat_checkedin}    value={`${stats.checkedIn} / ${stats.totalParticipants}`} color="green"
        sub={stats.totalParticipants ? `${Math.round(stats.checkedIn / stats.totalParticipants * 100)}%` : '0%'} />
      <StatCard icon={Building2} label={t.stat_companies}    value={stats.totalCompanies} color="purple" />
      <StatCard icon={UserCheck} label={t.stat_accepted_reg} value={stats.acceptedRegistrations} color="green"
        sub={`${stats.acceptanceRate}% ${t.stat_acceptance_rate}`} />
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      <StatCard icon={KeyRound} label={t.stat_free_codes} value={stats.freeCodesCount} color="blue" />
      <StatCard icon={Clock}    label={t.stat_new_reg}    value={stats.newRegistrations} color="orange"
        sub={`${stats.pendingRegistrations} ${t.stat_pending}`} />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-700 mb-4">{t.chart_company_types}</h3>
        <CompanyTypesChart data={companyTypes} t={t} />
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-700 mb-4">{t.reg_funnel_title}</h3>
        <RegistrationFunnel stats={stats} t={t} />
      </div>
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-700 mb-4">{t.top_countries_title}</h3>
        <TopCountries data={stats.topCountries} t={t} />
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-700 mb-4">{t.ov_chart_categories || 'Enjeux'}</h3>
        <HorizontalBarList data={stats.topCategories} t={t} />
      </div>
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <h3 className="font-semibold text-gray-700 mb-4">{t.ov_chart_requests || 'Demandes'}</h3>
        <HorizontalBarList data={stats.topRequests} t={t} />
      </div>
    </div>

    <ProgressCard label={t.profile_completion} rate={stats.profileCompletionRate} barColor="#2563eb"
      sub={t.profile_sub(stats.completeProfiles, stats.totalParticipants)} />

    {/* ═══════ SECTION MATCHMAKING ═══════ */}
    <SectionTitle icon={Target}>{t.ov_section_matchmaking}</SectionTitle>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      <StatCard icon={CheckCircle}   label={t.stat_confirmed}   value={stats.confirmedMeetings} color="green" />
      <StatCard icon={MessageSquare} label={t.stat_requests}     value={stats.totalRequests} color="orange"
        sub={`${stats.pendingRequests} ${t.stat_pending}`} />
      <StatCard icon={Calendar}      label={t.stat_avg_meetings} value={stats.avgMeetingsPerParticipant} color="indigo" />
      <StatCard icon={UserCheck}     label={t.stat_accepted_requests || 'Acceptées'} value={stats.acceptedRequests} color="blue" />
    </div>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
      <StatCard icon={XCircle}       label={t.stat_rejected_requests || 'Refusées'}  value={stats.rejectedRequests}  color="red" />
      <StatCard icon={X}             label={t.stat_cancelled_requests || 'Annulées'} value={stats.cancelledRequests} color="gray" />
    </div>

    <ProgressCard label={t.slot_occupation} rate={stats.slotOccupationRate} barColor="#22c55e"
      sub={t.slot_sub(stats.confirmedMeetings, stats.totalSlotCapacity)} />

    {/* ═══════ SECTION NETWORKING ═══════ */}
    <SectionTitle icon={Link2}>{t.ov_section_networking}</SectionTitle>

    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard icon={UserPlus}    label={t.stat_net_participants} value={stats.networkingUsers}  color="blue" />
      <StatCard icon={Link2}       label={t.stat_net_connected}    value={stats.netConnected}     color="green" />
      <StatCard icon={Clock}       label={t.stat_net_pending}      value={stats.netPending}       color="orange" />
      <StatCard icon={ThumbsDown}  label={t.stat_net_declined}     value={stats.netDeclined}      color="red" />
    </div>
  </div>
);

// ─── Participant Modal ────────────────────────────────────────────────────────

const ParticipantModal = ({ participant: p, onClose, t, meetings = [], slots = [], orgHeaders, eventName, subEventId, simpleView = false, onUpdate, onDelete }) => {
  const [sendState, setSendState] = useState('idle');
  const [editMode, setEditMode] = useState(null); // null | 'profile' | 'company'
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [availableActivities, setAvailableActivities] = useState([]);
  const [availableRequests, setAvailableRequests] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [selectedActivityIds, setSelectedActivityIds] = useState([]);
  const [selectedRequestIds, setSelectedRequestIds] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const handleEditChange = (field, value) => setEditData(prev => ({ ...prev, [field]: value }));

  const startEditProfile = () => {
    setEditData({
      name: p.name || '', job_title: p.job_title || '', phone: p.phone || '',
      country: p.country || '', linkedin: p.linkedin || '',
      activity: p.activity || '', bio: p.bio || '',
    });
    setEditMode('profile');
  };

  const startEditCompany = async () => {
    if (!p.company_id) return;
    setEditData({
      company: p.company || '', description: p.company_description || '',
      website: p.company_website || '', address: p.company_address || '',
      phone: p.company_phone || '', linkedin: p.company_linkedin || '',
    });
    setEditMode('company');
    setLoadingOptions(true);
    try {
      const [typRes, catRes, actRes, reqRes, coRes] = await Promise.all([
        fetch(`${API}/types`).then(r => r.json()),
        fetch(`${API}/categories/${subEventId}`).then(r => r.json()),
        fetch(`${API}/activities/${subEventId}`).then(r => r.json()),
        fetch(`${API}/requests-options`).then(r => r.json()),
        fetch(`${API}/company/${p.company_id}`).then(r => r.json()),
      ]);
      setAvailableTypes(typRes.data || []);
      setAvailableCategories(catRes.data || []);
      setAvailableActivities(actRes.data || []);
      setAvailableRequests(reqRes.data || []);
      const co = coRes.data || {};
      setSelectedTypeId(co.type_id || null);
      setSelectedCategoryIds(co.category_ids || []);
      setSelectedActivityIds(co.activity_ids || []);
      setSelectedRequestIds(co.request_ids || []);
    } catch { /* silent */ } finally { setLoadingOptions(false); }
  };

  const toggleId = (list, setList, id) => {
    setList(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editMode === 'profile') {
        const res = await fetch(`${API}/participants/${p.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...orgHeaders() },
          body: JSON.stringify(editData),
        });
        if (!res.ok) throw new Error();
        if (onUpdate) onUpdate(p.id, editData);
      } else if (editMode === 'company' && p.company_id) {
        const payload = {
          ...editData,
          type_id: selectedTypeId,
          category_ids: selectedCategoryIds,
          activity_ids: selectedActivityIds,
          request_ids: selectedRequestIds,
        };
        const res = await fetch(`${API}/company/${p.company_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...orgHeaders() },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error();
        const typeName = availableTypes.find(t2 => t2.id === selectedTypeId)?.name || '';
        const actNames = availableActivities.filter(a => selectedActivityIds.includes(a.id)).map(a => a.name);
        const reqNames = availableRequests.filter(r => selectedRequestIds.includes(r.id)).map(r => r.name);
        if (onUpdate) onUpdate(p.id, {
          company: editData.company, company_description: editData.description,
          company_website: editData.website, company_address: editData.address,
          company_phone: editData.phone, company_linkedin: editData.linkedin,
          company_type: typeName, company_activities: actNames, company_requests: reqNames,
        });
      }
      setEditMode(null);
    } catch { /* silent */ } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API}/participants/${p.id}`, {
        method: 'DELETE',
        headers: orgHeaders(),
      });
      if (!res.ok) throw new Error();
      if (onDelete) onDelete(p.id);
      onClose();
    } catch { setDeleting(false); setDeleteConfirm(false); }
  };

  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleSendAgenda = async () => {
    setSendState('sending');
    const pMeetings = meetings.filter(m =>
      m.status !== 'Cancelled' &&
      (m.requester_name === p.name || m.recipient_name === p.name)
    );
    const payload = {
      participant_name:    p.name,
      participant_email:   p.email,
      participant_company: p.company,
      participant_fonction: p.job_title || '',
      event_name:          eventName || '',
      sub_event_id:        subEventId || '',
      slots_total:         slots.length,
      meetings_count:      pMeetings.length,
      meetings: pMeetings.map(m => {
        const isRequester = m.requester_name === p.name;
        return {
          slot_time:     m.slot_time || m.slot_label || '',
          table:         m.table_label || '',
          other_name:    isRequester ? m.recipient_name    : m.requester_name,
          other_company: isRequester ? m.recipient_company : m.requester_company,
          other_role:    isRequester ? m.recipient_role    : m.requester_role,
        };
      }),
    };
    try {
      const res = await fetch(`${API}/send-agenda`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', ...orgHeaders() },
        body:    JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      setSendState('sent');
      setTimeout(() => setSendState('idle'), 4000);
    } catch {
      setSendState('error');
      setTimeout(() => setSendState('idle'), 4000);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ── Colored header with photo ── */}
        <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 p-6 pb-5 flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition rounded-full p-1 hover:bg-white/10"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-4 pr-8">
            {p.photo
              ? <img src={p.photo} alt={p.name} className="w-16 h-16 rounded-full object-cover border-2 border-white/40 flex-shrink-0" />
              : <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 select-none">
                  {(p.name || '?')[0].toUpperCase()}
                </div>
            }
            <div className="min-w-0">
              <h2 className="text-white font-bold text-lg leading-tight">{p.name}</h2>
              {p.job_title && <p className="text-blue-200 text-sm mt-0.5">{p.job_title}</p>}
              {p.company && (
                <span className="inline-block mt-1.5 bg-white/15 text-white text-xs px-2.5 py-0.5 rounded-full">
                  {p.company}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">

          {editMode === 'profile' ? (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.edit_field_name}</label>
                <input type="text" value={editData.name} onChange={e => handleEditChange('name', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.edit_field_job}</label>
                <input type="text" value={editData.job_title} onChange={e => handleEditChange('job_title', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</label>
                <input type="email" value={p.email || ''} disabled
                  className="mt-1 w-full px-3 py-2 border border-gray-100 rounded-xl text-sm bg-gray-50 text-gray-400 cursor-not-allowed" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.edit_field_phone}</label>
                <input type="text" value={editData.phone} onChange={e => handleEditChange('phone', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.edit_field_country}</label>
                <input type="text" value={editData.country} onChange={e => handleEditChange('country', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">LinkedIn</label>
                <input type="text" value={editData.linkedin} onChange={e => handleEditChange('linkedin', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.edit_field_activity}</label>
                <input type="text" value={editData.activity} onChange={e => handleEditChange('activity', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.edit_field_bio}</label>
                <textarea rows={4} value={editData.bio} onChange={e => handleEditChange('bio', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
            </div>
          ) : editMode === 'company' ? (
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.edit_field_company}</label>
                <input type="text" value={editData.company} onChange={e => handleEditChange('company', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.edit_field_company_desc}</label>
                <textarea rows={3} value={editData.description} onChange={e => handleEditChange('description', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.modal_company_website}</label>
                <input type="text" value={editData.website} onChange={e => handleEditChange('website', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.modal_company_address}</label>
                <input type="text" value={editData.address} onChange={e => handleEditChange('address', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.modal_company_phone}</label>
                <input type="text" value={editData.phone} onChange={e => handleEditChange('phone', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.modal_company_linkedin}</label>
                <input type="text" value={editData.linkedin} onChange={e => handleEditChange('linkedin', e.target.value)}
                  className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              {loadingOptions ? (
                <div className="flex justify-center py-4">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full" />
                </div>
              ) : (<>
                {availableTypes.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.modal_company_type}</label>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {availableTypes.map(tp => {
                        const sel = selectedTypeId === tp.id;
                        return (
                          <button key={tp.id} type="button" onClick={() => setSelectedTypeId(sel ? null : tp.id)}
                            className={`text-xs px-2.5 py-1 rounded-full font-medium transition ${
                              sel ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-blue-50 hover:text-blue-700'
                            }`}>{tp.name}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {availableCategories.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.edit_field_categories}</label>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {availableCategories.map(cat => {
                        const sel = selectedCategoryIds.includes(cat.id);
                        return (
                          <button key={cat.id} type="button" onClick={() => toggleId(selectedCategoryIds, setSelectedCategoryIds, cat.id)}
                            className={`text-xs px-2.5 py-1 rounded-full font-medium transition ${
                              sel ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-indigo-50 hover:text-indigo-700'
                            }`}>{cat.name}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {availableActivities.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.modal_company_activities}</label>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {availableActivities.map(a => {
                        const sel = selectedActivityIds.includes(a.id);
                        return (
                          <button key={a.id} type="button" onClick={() => toggleId(selectedActivityIds, setSelectedActivityIds, a.id)}
                            className={`text-xs px-2.5 py-1 rounded-full font-medium transition ${
                              sel ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-700'
                            }`}>{a.name}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
                {availableRequests.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{t.modal_company_requests}</label>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {availableRequests.map(r => {
                        const sel = selectedRequestIds.includes(r.id);
                        return (
                          <button key={r.id} type="button" onClick={() => toggleId(selectedRequestIds, setSelectedRequestIds, r.id)}
                            className={`text-xs px-2.5 py-1 rounded-full font-medium border transition ${
                              sel ? 'bg-orange-600 text-white border-orange-600' : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-orange-50 hover:text-orange-700'
                            }`}>{r.name}</button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>)}
            </div>
          ) : (<>

          {/* Contact row */}
          <div className="space-y-2.5">
            {p.email && (
              <a href={`mailto:${p.email}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-blue-600 transition group">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition">
                  <Mail size={14} className="text-blue-500" />
                </div>
                <span className="truncate">{p.email}</span>
              </a>
            )}
            {p.phone && (
              <a href={`tel:${p.phone}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-green-600 transition group">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-100 transition">
                  <Phone size={14} className="text-green-500" />
                </div>
                <span>{p.phone}</span>
              </a>
            )}
            {p.country && (
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                  <MapPin size={14} className="text-purple-500" />
                </div>
                <span>{p.country}</span>
              </div>
            )}
            {p.linkedin && (
              <a href={p.linkedin} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 text-sm text-blue-600 hover:text-blue-800 transition group">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition">
                  <ExternalLink size={14} className="text-blue-500" />
                </div>
                <span className="underline underline-offset-2">LinkedIn</span>
              </a>
            )}
          </div>

          {/* Personal activity / sector */}
          {!simpleView && p.activity && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t.modal_activity}</p>
              <p className="text-sm text-gray-700 bg-gray-50 rounded-xl px-3 py-2.5">{p.activity}</p>
            </div>
          )}

          {/* Bio */}
          {p.bio && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t.modal_bio}</p>
              <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl px-3 py-2.5 whitespace-pre-wrap">{p.bio}</p>
            </div>
          )}

          {/* ── Company section ── */}
          {(p.company_description || p.company_type || p.company_activities?.length > 0 || p.company_requests?.length > 0 || p.company_website || p.company_address || p.company_phone || p.company_linkedin) && (
            <div className="border-t border-gray-100 pt-4 space-y-3">
              {/* Company header */}
              <div className="flex items-center gap-3">
                {p.company_logo
                  ? <img src={p.company_logo} alt={p.company} className="w-10 h-10 rounded-lg object-contain border border-gray-100 bg-white p-0.5 flex-shrink-0" />
                  : <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      <Building2 size={18} className="text-gray-400" />
                    </div>
                }
                <div className="min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">{p.company}</p>
                  {p.company_type && (
                    <span className="inline-block text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full font-medium">
                      {p.company_type}
                    </span>
                  )}
                </div>
                {p.company_website && (
                  <a href={p.company_website.startsWith('http') ? p.company_website : `https://${p.company_website}`}
                    target="_blank" rel="noopener noreferrer"
                    className="ml-auto flex-shrink-0 text-xs text-blue-500 hover:text-blue-700 flex items-center gap-1 transition"
                    title={t.modal_company_website}
                  >
                    <ExternalLink size={12} />
                    <span className="hidden sm:inline">{t.modal_company_website}</span>
                  </a>
                )}
              </div>

              {/* Company description */}
              {p.company_description && (
                <p className="text-sm text-gray-600 leading-relaxed bg-gray-50 rounded-xl px-3 py-2.5 whitespace-pre-wrap">
                  {p.company_description}
                </p>
              )}

              {/* Address / Phone / LinkedIn company */}
              <div className="space-y-2">
                {p.company_address && (
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0">
                      <MapPin size={13} className="text-gray-400" />
                    </div>
                    <span>{p.company_address}</span>
                  </div>
                )}
                {p.company_phone && (
                  <a href={`tel:${p.company_phone}`} className="flex items-center gap-3 text-sm text-gray-600 hover:text-green-600 transition group">
                    <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-50 transition">
                      <Phone size={13} className="text-gray-400 group-hover:text-green-500" />
                    </div>
                    <span>{p.company_phone}</span>
                  </a>
                )}
                {p.company_linkedin && (
                  <a href={p.company_linkedin} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-3 text-sm text-blue-600 hover:text-blue-800 transition group">
                    <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition">
                      <ExternalLink size={13} className="text-blue-500" />
                    </div>
                    <span className="underline underline-offset-2">LinkedIn</span>
                  </a>
                )}
              </div>

              {/* Activity sectors */}
              {!simpleView && p.company_activities?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t.modal_company_activities}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.company_activities.map((act, i) => (
                      <span key={i} className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-full font-medium">
                        {act}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Requests / looking for */}
              {!simpleView && p.company_requests?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t.modal_company_requests}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {p.company_requests.map((req, i) => (
                      <span key={i} className="text-xs bg-orange-50 text-orange-700 px-2.5 py-1 rounded-full font-medium border border-orange-100">
                        {req}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!p.email && !p.phone && !p.country && !p.linkedin && !p.activity && !p.bio && !p.company_description && !p.company_requests?.length && !p.company_address && !p.company_phone && (
            <p className="text-center text-gray-400 py-6 text-sm">{t.profile_nok}</p>
          )}

          {/* ── Agenda ─────────────────────────────────────────────────────── */}
          {(() => {
            const pMeetings = meetings.filter(m =>
              m.status !== 'Cancelled' &&
              (m.requester_name === p.name || m.recipient_name === p.name)
            );
            const meetingBySlot = {};
            pMeetings.forEach(m => {
              const key = (m.slot_date || '') + (m.slot_start || '');
              if (key) meetingBySlot[key] = m;
            });
            return (
              <div className="border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{t.modal_agenda}</p>
                {pMeetings.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-2">{t.modal_agenda_no_slots}</p>
                ) : (
                  <div className="space-y-2">
                    {pMeetings.map(m => {
                      const isRequester = m.requester_name === p.name;
                      const other   = isRequester ? m.recipient_name    : m.requester_name;
                      const otherCo = isRequester ? m.recipient_company : m.requester_company;
                      return (
                        <div key={m.id} className="flex items-start gap-3 p-2.5 rounded-xl bg-blue-50">
                          <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 bg-blue-500" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500">{m.slot_time || m.slot_label || '—'}</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium text-gray-900">{other}</p>
                              {otherCo && <p className="text-xs text-gray-500">{otherCo}</p>}
                              {m.table_label && (
                                <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium">{m.table_label}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          </>)}
        </div>

        {/* ── Footer ── */}
        {!simpleView && (
          <div className="flex-shrink-0 border-t border-gray-100 p-4 bg-gray-50">
            {deleteConfirm ? (
              <div className="flex items-center gap-3">
                <p className="flex-1 text-sm text-red-600 font-medium">{t.edit_delete_confirm}</p>
                <button onClick={handleDelete} disabled={deleting}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50">
                  {deleting ? '…' : t.edit_delete_yes}
                </button>
                <button onClick={() => setDeleteConfirm(false)} disabled={deleting}
                  className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition">
                  {t.edit_delete_no}
                </button>
              </div>
            ) : editMode ? (
              <div className="flex gap-2">
                <button onClick={handleSave} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50">
                  <Save size={15} />
                  {saving ? t.edit_saving : t.edit_save}
                </button>
                <button onClick={() => setEditMode(null)} disabled={saving}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition">
                  {t.edit_cancel}
                </button>
              </div>
            ) : (
              <div className="flex gap-2 flex-wrap">
                {onUpdate && (
                  <button onClick={startEditProfile}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition">
                    <Pencil size={14} />
                    {t.edit_btn_profile}
                  </button>
                )}
                {onUpdate && p.company_id && (
                  <button onClick={startEditCompany}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-purple-50 text-purple-700 hover:bg-purple-100 transition">
                    <Building2 size={14} />
                    {t.edit_btn_company}
                  </button>
                )}
                {onDelete && (
                  <button onClick={() => setDeleteConfirm(true)}
                    className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-red-50 text-red-600 hover:bg-red-100 transition">
                    <Trash2 size={15} />
                    {t.edit_delete_btn}
                  </button>
                )}
                <button
                  onClick={handleSendAgenda}
                  disabled={sendState === 'sending' || sendState === 'sent' || !p.email}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition ${
                    sendState === 'sent'
                      ? 'bg-green-100 text-green-700 cursor-default'
                      : sendState === 'error'
                      ? 'bg-red-50 text-red-600'
                      : sendState === 'sending'
                      ? 'bg-blue-50 text-blue-500 cursor-wait'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  <Mail size={15} />
                  {sendState === 'sending' ? t.send_agenda_sending
                    : sendState === 'sent'  ? t.send_agenda_ok
                    : sendState === 'error' ? t.send_agenda_error
                    : t.send_agenda_btn}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Registration User Modal ──────────────────────────────────────────────────

const RegistrationUserModal = ({ user: u, onClose, onUpdateStatus, updatingId, t }) => {
  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const reg = u.registration || 'New';
  const isUpdating = updatingId === u.id;
  const statusLabel = (s) => t[`pv_status_${s?.toLowerCase()}`] || s;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 p-6 pb-5 flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition rounded-full p-1 hover:bg-white/10"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-4 pr-8">
            <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-white text-2xl font-bold flex-shrink-0 select-none">
              {(u.full_name || '?')[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <h2 className="text-white font-bold text-lg leading-tight">{u.full_name}</h2>
              {u.fonction && <p className="text-blue-200 text-sm mt-0.5">{u.fonction}</p>}
              {u.company && (
                <span className="inline-block mt-1.5 bg-white/15 text-white text-xs px-2.5 py-0.5 rounded-full">
                  {u.company}
                </span>
              )}
            </div>
          </div>
          {/* Status badge in header */}
          <div className="mt-4">
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${REG_STATUS_STYLE[reg] || 'bg-gray-100 text-gray-600'}`}>
              {statusLabel(reg)}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Contact */}
          <div className="space-y-2.5">
            {u.email && (
              <a href={`mailto:${u.email}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-blue-600 transition group">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition">
                  <Mail size={14} className="text-blue-500" />
                </div>
                <span className="truncate">{u.email}</span>
              </a>
            )}
            {u.phone && (
              <a href={`tel:${u.phone}`} className="flex items-center gap-3 text-sm text-gray-700 hover:text-green-600 transition group">
                <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0 group-hover:bg-green-100 transition">
                  <Phone size={14} className="text-green-500" />
                </div>
                <span>{u.phone}</span>
              </a>
            )}
            {u.country && (
              <div className="flex items-center gap-3 text-sm text-gray-700">
                <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                  <MapPin size={14} className="text-purple-500" />
                </div>
                <span>{u.country}</span>
              </div>
            )}
            {u.linkedin && (
              <a href={u.linkedin} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 text-sm text-blue-600 hover:text-blue-800 transition group">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition">
                  <ExternalLink size={14} className="text-blue-500" />
                </div>
                <span className="underline underline-offset-2">LinkedIn</span>
              </a>
            )}
          </div>

          {/* Description / motivation */}
          {u.description && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">{t.modal_bio}</p>
              <p className="text-sm text-gray-700 leading-relaxed bg-gray-50 rounded-xl px-3 py-2.5 whitespace-pre-wrap">{u.description}</p>
            </div>
          )}
        </div>

        {/* Footer — action buttons */}
        <div className="flex-shrink-0 border-t border-gray-100 p-4 flex gap-2 flex-wrap justify-center bg-gray-50">
          {['Accepted', 'Declined', 'Pending'].map(s => (
            <button
              key={s}
              disabled={isUpdating || reg === s}
              onClick={() => onUpdateStatus(u.id, s)}
              className={`flex-1 min-w-0 px-3 py-2 rounded-xl text-sm font-semibold transition ${
                reg === s
                  ? s === 'Accepted' ? 'bg-green-600 text-white cursor-default'
                    : s === 'Declined' ? 'bg-red-500 text-white cursor-default'
                    : 'bg-orange-500 text-white cursor-default'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-100 disabled:opacity-50'
              }`}
            >
              {isUpdating && reg !== s ? '…' : t[`pv_btn_${s.toLowerCase()}`]}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Tab: Pending Validation ──────────────────────────────────────────────────

const REG_STATUS_STYLE = {
  New:      'bg-gray-100 text-gray-600',
  Pending:  'bg-orange-100 text-orange-600',
  Accepted: 'bg-green-100 text-green-700',
  Declined: 'bg-red-100 text-red-600',
};

const PendingValidationTab = ({ registrationUsers, setRegistrationUsers, orgHeaders, t }) => {
  const [search, setSearch]           = useState('');
  const [filter, setFilter]           = useState('all');
  const [updatingId, setUpdatingId]   = useState(null);
  const [errorId, setErrorId]         = useState(null);
  const [errorMsg, setErrorMsg]       = useState({});
  const [selectedUser, setSelectedUser] = useState(null);

  const filtered = registrationUsers.filter(u => {
    const matchSearch = !search ||
      `${u.full_name} ${u.company} ${u.email} ${u.fonction}`.toLowerCase().includes(search.toLowerCase());
    if (filter === 'all') return matchSearch;
    return matchSearch && u.registration === filter;
  });

  const updateStatus = async (userId, status) => {
    setUpdatingId(userId);
    setErrorId(null);
    setErrorMsg(prev => ({ ...prev, [userId]: null }));
    const prev_status = registrationUsers.find(u => u.id === userId)?.registration;
    // Optimistic update
    setRegistrationUsers(prev => prev.map(u => u.id === userId ? { ...u, registration: status } : u));
    if (selectedUser?.id === userId) setSelectedUser(u => ({ ...u, registration: status }));
    try {
      const res = await fetch(`${API}/registration-users/${userId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...orgHeaders() },
        body: JSON.stringify({ registration: status }),
      });
      if (!res.ok) {
        const msg = res.status === 409 ? t.pv_no_free_code : t.pv_save_error;
        throw new Error(msg);
      }
    } catch (err) {
      // Rollback on error
      setRegistrationUsers(prev => prev.map(u => u.id === userId ? { ...u, registration: prev_status } : u));
      if (selectedUser?.id === userId) setSelectedUser(u => ({ ...u, registration: prev_status }));
      setErrorId(userId);
      setErrorMsg(prev => ({ ...prev, [userId]: err.message }));
    } finally {
      setUpdatingId(null);
    }
  };

  const statusLabel = (s) => t[`pv_status_${s?.toLowerCase()}`] || s;
  const filterLabels = {
    all: t.pv_filter_all,
    New: t.pv_filter_new,
    Accepted: t.pv_filter_accepted,
    Declined: t.pv_filter_declined,
    Pending: t.pv_filter_pending,
  };

  return (
    <div>
      {selectedUser && (
        <RegistrationUserModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onUpdateStatus={updateStatus}
          updatingId={updatingId}
          t={t}
        />
      )}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input type="text" placeholder={t.pv_search} value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'New', 'Accepted', 'Declined', 'Pending'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold transition ${
                filter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {filterLabels[f]}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <th className="text-left px-4 py-3">{t.pv_col_name}</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">{t.pv_col_company}</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">{t.pv_col_email}</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">{t.pv_col_country}</th>
                <th className="text-center px-4 py-3">{t.pv_col_status}</th>
                <th className="text-center px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, i) => {
                const reg = u.registration || 'New';
                const isUpdating = updatingId === u.id;
                return (
                  <tr
                    key={u.id}
                    onClick={() => setSelectedUser(u)}
                    className={`border-b border-gray-50 hover:bg-blue-50/40 transition cursor-pointer ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{u.full_name}</p>
                      {u.fonction && <p className="text-xs text-gray-400">{u.fonction}</p>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{u.company || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 hidden md:table-cell text-xs">{u.email}</td>
                    <td className="px-4 py-3 text-gray-600 hidden lg:table-cell">{u.country || '—'}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${REG_STATUS_STYLE[reg] || 'bg-gray-100 text-gray-600'}`}>
                        {statusLabel(reg)}
                      </span>
                      {errorId === u.id && (
                        <p className="text-xs text-red-500 mt-1">{errorMsg[u.id] || t.pv_save_error}</p>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1.5 flex-wrap">
                        {['Accepted', 'Declined', 'Pending'].map(s => (
                          <button
                            key={s}
                            disabled={isUpdating || reg === s}
                            onClick={() => updateStatus(u.id, s)}
                            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition ${
                              reg === s
                                ? s === 'Accepted' ? 'bg-green-600 text-white cursor-default'
                                  : s === 'Declined' ? 'bg-red-500 text-white cursor-default'
                                  : 'bg-orange-500 text-white cursor-default'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50'
                            }`}
                          >
                            {isUpdating && reg !== s ? '…' : t[`pv_btn_${s.toLowerCase()}`]}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">{t.pv_no_results}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Wrapper: Participants avec sous-onglets ───────────────────────────────────

const ParticipantsWithSubTabs = ({ participants, registrationUsers, setRegistrationUsers, orgHeaders, t, meetings, slots, eventName, subEventId, eventSlug }) => {
  const [subTab, setSubTab] = useState('participants');
  const [sendAllState, setSendAllState] = useState('idle'); // idle | sending | done | error
  const [sendAllCount, setSendAllCount] = useState(0);
  const newCount = registrationUsers.filter(u => u.registration === 'New').length;

  const handleExportParticipants = () => {
    const cols = [
      t.export_col_lastname   || 'Nom',
      t.export_col_firstname  || 'Prénom',
      t.export_col_job        || 'Fonction',
      t.export_col_email      || 'Email',
      t.export_col_company    || 'Entreprise',
      t.export_col_phone      || 'Téléphone',
      t.export_col_registered || "Date d'inscription",
      'QrCode-url',
    ];
    const rows = participants.map(p => [
      p.lastname   || '',
      p.firstname  || '',
      p.job_title  || '',
      p.email      || '',
      p.company    || '',
      p.phone      || '',
      p.created_at ? p.created_at.slice(0, 10) : '',
      eventSlug && p.id ? `https://matchmaking.mobivent.fr/event/${eventSlug}/participants-networking/${p.id}` : '',
    ]);
    const csv = [cols, ...rows]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';'))
      .join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = t.export_participants_filename || 'participants.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSendAll = async () => {
    if (!window.confirm(t.send_all_confirm(participants.length))) return;
    setSendAllState('sending');
    try {
      const res = await fetch(`${API}/send-agenda-all/${subEventId}`, {
        method: 'POST',
        headers: orgHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setSendAllCount(data.sent || participants.length);
      setSendAllState('done');
      setTimeout(() => setSendAllState('idle'), 6000);
    } catch {
      setSendAllState('error');
      setTimeout(() => setSendAllState('idle'), 4000);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-1 mb-5 border-b border-gray-200">
        <button
          onClick={() => setSubTab('pending')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold transition border-b-2 -mb-px ${
            subTab === 'pending'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          {t.tab_pending_validation}
          {newCount > 0 && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
              subTab === 'pending' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-600'
            }`}>{newCount}</span>
          )}
        </button>
        <button
          onClick={() => setSubTab('participants')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold transition border-b-2 -mb-px ${
            subTab === 'participants'
              ? 'border-blue-600 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          {t.tab_participants}
          {participants.length > 0 && (
            <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
              subTab === 'participants' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
            }`}>{participants.length}</span>
          )}
        </button>
      </div>

      {/* Boutons — visible sur l'onglet participants */}
      {subTab === 'participants' && participants.length > 0 && (
        <div className="mb-4 flex justify-end gap-2">
          <button
            onClick={handleExportParticipants}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          >
            <Download size={15} />
            {t.export_participants_btn || 'Export participants Excel'}
          </button>
          <button
            onClick={handleSendAll}
            disabled={sendAllState === 'sending' || sendAllState === 'done'}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition ${
              sendAllState === 'done'
                ? 'bg-green-100 text-green-700 cursor-default'
                : sendAllState === 'error'
                ? 'bg-red-50 text-red-600'
                : sendAllState === 'sending'
                ? 'bg-blue-50 text-blue-500 cursor-wait'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            <Mail size={15} />
            {sendAllState === 'sending' ? t.send_all_sending
              : sendAllState === 'done'  ? t.send_all_ok(sendAllCount)
              : sendAllState === 'error' ? t.send_all_error
              : t.send_all_btn}
          </button>
        </div>
      )}

      {subTab === 'pending'
        ? <PendingValidationTab
            registrationUsers={registrationUsers}
            setRegistrationUsers={setRegistrationUsers}
            orgHeaders={orgHeaders}
            t={t}
          />
        : <ParticipantsTab participants={participants} t={t} meetings={meetings} slots={slots} orgHeaders={orgHeaders} eventName={eventName} subEventId={subEventId} />
      }
    </div>
  );
};

// ─── Tab: Participants ────────────────────────────────────────────────────────

const ParticipantsTab = ({ participants: initialParticipants, t, meetings = [], slots = [], orgHeaders, eventName, subEventId }) => {
  const [participants, setParticipants] = useState(initialParticipants);
  const [search, setSearch]             = useState('');
  const [filter, setFilter]             = useState('all');
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [checkinLoading, setCheckinLoading] = useState(null); // recordId en cours
  const { sortKey, sortDir, toggle } = useSort('name');

  // Sync si les données parent changent
  React.useEffect(() => { setParticipants(initialParticipants); }, [initialParticipants]);

  const handleCheckin = async (p, e) => {
    e.stopPropagation();
    const newStatus = p.checkin_status === 'Checked In' ? 'Not Checked In' : 'Checked In';
    setCheckinLoading(p.id);
    try {
      const res = await fetch(`${API}/checkin/${p.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...orgHeaders() },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error();
      setParticipants(prev => prev.map(x => x.id === p.id ? { ...x, checkin_status: newStatus } : x));
    } catch {
      // silencieux
    } finally {
      setCheckinLoading(null);
    }
  };

  const base = participants.filter(p => {
    const matchSearch = !search || `${p.name} ${p.company} ${p.email}`.toLowerCase().includes(search.toLowerCase());
    const isComplete = p.photo && p.bio && p.job_title;
    if (filter === 'complete')   return matchSearch && isComplete;
    if (filter === 'incomplete') return matchSearch && !isComplete;
    return matchSearch;
  });

  const profileVal = (p) => (p.photo && p.bio && p.job_title) ? '1' : '0';
  const filtered = applySortStr(base, sortKey, sortDir,
    (p, k) => k === 'profile' ? profileVal(p) : p[k]);

  const filterLabels = { all: t.filter_all, complete: t.filter_complete, incomplete: t.filter_incomplete };
  const thProps = { sortKey, sortDir, onSort: toggle };

  const checkinBadge = (status) => {
    if (status === 'Checked In')     return <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full"><CheckCircle size={11} /> {t.checkin_done}</span>;
    if (status === 'Cancelled')      return <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full"><XCircle size={11} /> {t.checkin_cancelled}</span>;
    return <span className="text-xs text-gray-400">{t.checkin_pending}</span>;
  };

  return (
    <div>
      {selectedParticipant && (
        <ParticipantModal
          participant={selectedParticipant}
          onClose={() => setSelectedParticipant(null)}
          t={t}
          meetings={meetings}
          slots={slots}
          orgHeaders={orgHeaders}
          eventName={eventName}
          subEventId={subEventId}
          onUpdate={(id, data) => {
            setParticipants(prev => prev.map(x => x.id === id ? { ...x, ...data } : x));
            setSelectedParticipant(prev => prev ? { ...prev, ...data } : prev);
          }}
          onDelete={(id) => {
            setParticipants(prev => prev.filter(x => x.id !== id));
            setSelectedParticipant(null);
          }}
        />
      )}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input type="text" placeholder={t.search_participant} value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <div className="flex gap-2">
          {['all','complete','incomplete'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-xl text-xs font-semibold transition ${
                filter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {filterLabels[f]}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <Th col="name"           label={t.col_participant}  {...thProps} className="text-left px-4 py-3" />
                <Th col="company"        label={t.col_company}      {...thProps} className="text-left px-4 py-3 hidden sm:table-cell" />
                <Th col="country"        label={t.col_country}      {...thProps} className="text-left px-4 py-3 hidden md:table-cell" />
                <Th col="checkin_status" label={t.col_checkin}      {...thProps} className="text-center px-4 py-3" />
                <Th col="profile"        label={t.col_profile}      {...thProps} className="text-center px-4 py-3 hidden lg:table-cell" />
                <th className="px-4 py-3 w-24" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((p, i) => {
                const isComplete = p.photo && p.bio && p.job_title;
                return (
                  <tr
                    key={p.id}
                    onClick={() => setSelectedParticipant(p)}
                    className={`border-b border-gray-50 hover:bg-blue-50/40 transition cursor-pointer ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {p.photo
                          ? <img src={p.photo} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                          : <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xs font-bold flex-shrink-0">
                              {(p.name || '?')[0].toUpperCase()}
                            </div>
                        }
                        <div>
                          <p className="font-medium text-gray-900">{p.name}</p>
                          <p className="text-xs text-gray-400">{p.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{p.company || '—'}</td>
                    <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{p.country || '—'}</td>
                    <td className="px-4 py-3 text-center">{checkinBadge(p.checkin_status)}</td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell">
                      {isComplete
                        ? <span className="inline-flex items-center gap-1 text-xs text-green-600 font-medium"><CheckCircle size={13} /> {t.profile_ok}</span>
                        : <span className="inline-flex items-center gap-1 text-xs text-orange-500 font-medium"><AlertTriangle size={13} /> {t.profile_nok}</span>
                      }
                    </td>
                    <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                      {p.checkin_status === 'Checked In' ? (
                        <button
                          onClick={e => handleCheckin(p, e)}
                          disabled={checkinLoading === p.id}
                          className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600 transition disabled:opacity-40"
                        >
                          {checkinLoading === p.id ? '…' : t.checkin_undo}
                        </button>
                      ) : (
                        <button
                          onClick={e => handleCheckin(p, e)}
                          disabled={checkinLoading === p.id || p.checkin_status === 'Cancelled'}
                          className="px-2.5 py-1 text-xs font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-40"
                        >
                          {checkinLoading === p.id ? '…' : t.checkin_btn}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">{t.no_participants}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Tab: Agenda (meetings grid) ─────────────────────────────────────────────

const AgendaTab = ({ meetings, slots, t, orgHeaders, onMeetingUpdate, participants = [] }) => {
  const [search, setSearch]             = useState('');
  const [cancellingId, setCancellingId] = useState(null);
  const [cancelError, setCancelError]   = useState(null);
  const [modalParticipant, setModalParticipant] = useState(null);
  const { sortKey, sortDir, toggle } = useSort('slot_time');

  const base = meetings.filter(m => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (m.requester_name + m.recipient_name + m.requester_company + m.recipient_company).toLowerCase().includes(s);
  });
  const filtered = applySortStr(base, sortKey, sortDir);
  const thProps = { sortKey, sortDir, onSort: toggle };

  // Retrouve un participant par son nom dans la liste
  const findParticipant = (name) => participants.find(p => p.name === name) || null;

  // Export CSV (ouvre dans Excel)
  const exportCSV = () => {
    const lookupEmail = (name) => participants.find(p => p.name === name)?.email || '';
    const lookupPhone = (name) => participants.find(p => p.name === name)?.phone || '';
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const headers = ['Slot', 'Table', 'Participant A', 'Company A', 'Email A', 'Phone A', 'Participant B', 'Company B', 'Email B', 'Phone B', 'Status'];
    const rows = meetings.map(m => [
      m.slot_time || m.slot_label || '',
      m.table_label || '',
      m.requester_name || '',
      m.requester_company || '',
      lookupEmail(m.requester_name),
      lookupPhone(m.requester_name),
      m.recipient_name || '',
      m.recipient_company || '',
      lookupEmail(m.recipient_name),
      lookupPhone(m.recipient_name),
      m.status || '',
    ]);
    const csv = '\uFEFF' + [headers, ...rows].map(r => r.map(escape).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'agenda.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCancel = async (meetingId) => {
    setCancellingId(meetingId);
    setCancelError(null);
    try {
      const res = await fetch(`${API}/meetings/${meetingId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...orgHeaders() },
        body: JSON.stringify({ status: 'Cancelled' }),
      });
      if (!res.ok) throw new Error();
      onMeetingUpdate(meetingId, { status: 'Cancelled' });
    } catch {
      setCancelError(meetingId);
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <div>
      {modalParticipant && (
        <ParticipantModal
          participant={modalParticipant}
          onClose={() => setModalParticipant(null)}
          t={t}
          meetings={meetings}
          slots={slots}
          orgHeaders={orgHeaders}
          simpleView
        />
      )}
      {cancelError && (
        <div className="mb-4 px-4 py-2.5 bg-red-50 text-red-600 text-sm rounded-xl border border-red-100">
          {t.meeting_cancel_error}
        </div>
      )}
      <div className="flex items-center gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} />
          <input type="text" placeholder={t.search_meeting} value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        </div>
        <button
          onClick={exportCSV}
          className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-semibold rounded-xl hover:bg-green-700 transition flex-shrink-0"
        >
          <Download size={14} />
          {t.export_excel}
        </button>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                <Th col="slot_time"      label={t.col_slot}   {...thProps} className="text-left px-4 py-3" />
                <Th col="table_label"    label={t.col_table}  {...thProps} className="text-left px-4 py-3" />
                <Th col="requester_name" label={t.col_part_a} {...thProps} className="text-left px-4 py-3" />
                <Th col="recipient_name" label={t.col_part_b} {...thProps} className="text-left px-4 py-3" />
                <Th col="status"         label={t.col_status} {...thProps} className="text-center px-4 py-3" />
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => {
                const isCancelled = m.status === 'Cancelled';
                return (
                  <tr key={m.id} className={`border-b border-gray-50 transition ${isCancelled ? 'opacity-50' : 'hover:bg-gray-50'} ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                    <td className={`px-4 py-3 text-gray-600 whitespace-nowrap ${isCancelled ? 'line-through' : ''}`}>{m.slot_time || m.slot_label || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{m.table_label || '—'}</td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => { const p = findParticipant(m.requester_name); if (p) setModalParticipant(p); }}
                        className={`text-left font-medium ${isCancelled ? 'line-through text-gray-400' : 'text-gray-900 hover:text-blue-600 hover:underline'} transition`}
                      >{m.requester_name || '—'}</button>
                      <p className="text-xs text-gray-400">{m.requester_company || ''}</p>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => { const p = findParticipant(m.recipient_name); if (p) setModalParticipant(p); }}
                        className={`text-left font-medium ${isCancelled ? 'line-through text-gray-400' : 'text-gray-900 hover:text-blue-600 hover:underline'} transition`}
                      >{m.recipient_name || '—'}</button>
                      <p className="text-xs text-gray-400">{m.recipient_company || ''}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${statusColor[m.status] || 'bg-gray-100 text-gray-500'}`}>
                        {isCancelled ? t.meeting_cancelled_label : m.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {!isCancelled && (
                        <button
                          title={t.meeting_cancel}
                          disabled={cancellingId === m.id}
                          onClick={() => handleCancel(m.id)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-40"
                        >
                          {cancellingId === m.id
                            ? <span className="text-xs">…</span>
                            : <XCircle size={15} />
                          }
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="text-center py-10 text-gray-400">{t.no_meetings}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── Tab: Tables ─────────────────────────────────────────────────────────────

const TablesTab = ({ meetings, slots, t }) => {
  // Construire la grille tables × créneaux
  const tableSet = [...new Set(meetings.map(m => m.table_label).filter(Boolean))].sort();
  const slotSet  = [...new Set(
    [...meetings.map(m => m.slot_time || m.slot_label), ...slots.map(s => s.label || s.time)]
    .filter(Boolean)
  )].sort();

  const grid = {}; // grid[table][slot] = meeting | null
  tableSet.forEach(t => { grid[t] = {}; });
  meetings.forEach(m => {
    const t = m.table_label;
    const s = m.slot_time || m.slot_label;
    if (t && s) grid[t][s] = m;
  });

  if (tableSet.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <Table2 size={48} className="mx-auto mb-3 opacity-30" />
        <p>{t.no_tables}</p>
        <p className="text-xs mt-1">{t.no_tables_sub}</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-lg font-bold text-gray-800 mb-5">{t.tables_title}</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr>
              <th className="text-left p-3 bg-gray-50 border border-gray-200 font-semibold text-gray-600 w-24 rounded-tl-xl">{t.col_table_h}</th>
              {slotSet.map(s => (
                <th key={s} className="p-3 bg-gray-50 border border-gray-200 font-semibold text-gray-600 text-center whitespace-nowrap min-w-28">{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableSet.map((table, ti) => (
              <tr key={table}>
                <td className={`p-3 border border-gray-200 font-semibold text-gray-700 bg-gray-50 ${ti === tableSet.length - 1 ? 'rounded-bl-xl' : ''}`}>
                  {table}
                </td>
                {slotSet.map(slot => {
                  const meeting = grid[table]?.[slot];
                  return (
                    <td key={slot} className="p-2 border border-gray-200 align-top">
                      {meeting ? (
                        <div className={`rounded-lg p-2 text-xs ${
                          meeting.status === 'Confirmed' ? 'bg-green-50 border border-green-200' :
                          meeting.status === 'Pending'   ? 'bg-yellow-50 border border-yellow-200' :
                          'bg-gray-50 border border-gray-200'
                        }`}>
                          <p className="font-semibold text-gray-800 truncate">{meeting.requester_name}</p>
                          <p className="text-gray-500 truncate">↔ {meeting.recipient_name}</p>
                          <span className={`inline-block mt-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium ${statusColor[meeting.status] || 'bg-gray-100 text-gray-500'}`}>
                            {meeting.status}
                          </span>
                        </div>
                      ) : (
                        <div className="h-12 flex items-center justify-center text-gray-300">
                          <span className="text-lg">—</span>
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Tab: Requests ────────────────────────────────────────────────────────────

const RequestsTab = ({ requests, t }) => {
  const [filter, setFilter] = useState('all');
  const { sortKey, sortDir, toggle } = useSort('created_date');

  const base = filter === 'all' ? requests : requests.filter(r => r.status === filter);
  const filtered = applySortStr(base, sortKey, sortDir);

  const sortButtons = [
    { key: 'requester_name', label: 'A → Z' },
    { key: 'status',         label: t.col_status },
    { key: 'created_date',   label: 'Date' },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {['all','Pending','Accepted','Confirmed','Rejected','Cancelled'].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition ${
              filter === f ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}>
            {f === 'all'
              ? t.req_all(requests.length)
              : t.req_filter(f, requests.filter(r => r.status === f).length)
            }
          </button>
        ))}
        {/* Tri */}
        <div className="ml-auto flex items-center gap-1">
          {sortButtons.map(({ key, label }) => (
            <button key={key} onClick={() => toggle(key)}
              className={`flex items-center gap-0.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition ${
                sortKey === key ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}>
              {label}
              <SortIcon col={key} sortKey={sortKey} sortDir={sortDir} />
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        {filtered.map(r => (
          <div key={r.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-gray-900 text-sm">{r.requester_name || '—'}</span>
                <span className="text-gray-400 text-xs">{r.requester_company}</span>
                <span className="text-gray-400">→</span>
                <span className="font-semibold text-gray-900 text-sm">{r.recipient_name || '—'}</span>
                <span className="text-gray-400 text-xs">{r.recipient_company}</span>
              </div>
              {r.message && <p className="text-xs text-gray-400 mt-1 truncate">"{r.message}"</p>}
            </div>
            <span className={`flex-shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${statusColor[r.status] || 'bg-gray-100 text-gray-500'}`}>
              {r.status}
            </span>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-gray-400">{t.no_requests}</div>
        )}
      </div>
    </div>
  );
};

// ─── Tab: Codes ───────────────────────────────────────────────────────────────

const CodesTab = ({ codes: initialCodes, orgHeaders, t }) => {
  const [codes, setCodes] = useState(initialCodes);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const { sortKey, sortDir, toggle } = useSort('code');
  // Édition inline du Max
  const [editingMax, setEditingMax] = useState(null);
  const [editMaxVal, setEditMaxVal] = useState('');
  const [savingMax, setSavingMax] = useState(null);
  // Édition inline du Statut
  const [savingStatus, setSavingStatus] = useState(null);
  // Erreur visible
  const [saveError, setSaveError] = useState('');

  // Sync si les codes props changent (refresh)
  React.useEffect(() => { setCodes(initialCodes); }, [initialCodes]);

  // Dérive la company depuis les utilisateurs liés
  const getCompany = (c) => {
    const companies = [...new Set((c.users || []).map(u => u.company).filter(Boolean))];
    return companies.length ? companies.join(', ') : null;
  };

  const codeGetter = (c, k) => {
    if (k === 'company')     return getCompany(c) || '';
    if (k === 'registrants') return String(c.users?.length || 0);
    if (k === 'used')        return String(c.used_count || 0);
    if (k === 'max')         return String(c.max_participants || 0);
    return c[k] ?? '';
  };

  const base = codes.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.code || '').toLowerCase().includes(q) ||
           (getCompany(c) || '').toLowerCase().includes(q);
  });
  const filtered = applySortStr(base, sortKey, sortDir, codeGetter);
  const thProps = { sortKey, sortDir, onSort: toggle };

  const statusBadge = (status) => {
    const map = {
      Active:   'bg-green-100 text-green-700',
      Inactive: 'bg-gray-100 text-gray-500',
    };
    const cls = map[status] || 'bg-yellow-50 text-yellow-600';
    const label = status || 'Nouveau';
    return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{label}</span>;
  };

  // Sauvegarde du Max_Participants
  const saveMax = async (recordId) => {
    const val = parseInt(editMaxVal, 10);
    if (isNaN(val) || val < 0) { setEditingMax(null); return; }
    setSavingMax(recordId);
    setSaveError('');
    try {
      const res = await fetch(`/api/registration-codes/${recordId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...orgHeaders() },
        body: JSON.stringify({ max_participants: val }),
      });
      if (res.ok) {
        setCodes(prev => prev.map(c => c.id === recordId ? { ...c, max_participants: val } : c));
      } else {
        setSaveError(t.save_error);
      }
    } catch {
      setSaveError(t.save_error);
    } finally {
      setSavingMax(null);
      setEditingMax(null);
    }
  };

  const startEditMax = (e, c) => {
    e.stopPropagation();
    setSaveError('');
    setEditingMax(c.id);
    setEditMaxVal(String(c.max_participants || ''));
  };

  // Bascule Active ↔ Disabled
  const toggleStatus = async (e, c) => {
    e.stopPropagation();
    const newStatus = (c.status === 'Active') ? 'Disabled' : 'Active';
    setSavingStatus(c.id);
    setSaveError('');
    try {
      const res = await fetch(`/api/registration-codes/${c.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...orgHeaders() },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        setCodes(prev => prev.map(x => x.id === c.id ? { ...x, status: newStatus } : x));
      } else {
        setSaveError(t.save_error);
      }
    } catch {
      setSaveError(t.save_error);
    } finally {
      setSavingStatus(null);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-bold text-gray-800 mb-5">{t.codes_title}</h2>

      {/* Erreur sauvegarde */}
      {saveError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-sm text-red-700">
          <AlertTriangle size={15} className="flex-shrink-0" />
          {saveError}
          <button onClick={() => setSaveError('')} className="ml-auto text-red-400 hover:text-red-600">✕</button>
        </div>
      )}

      {/* Stats row */}
      {(() => {
        const totalMax  = codes.reduce((s, c) => s + (c.max_participants || 0), 0);
        const totalUsed = codes.reduce((s, c) => s + (c.used_count || 0), 0);
        const remaining = totalMax > 0 ? totalMax - totalUsed : null;
        return (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{codes.length}</p>
              <p className="text-sm text-gray-500">{t.stat_total_codes}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-green-600">
                {codes.filter(c => c.status === 'Active' || !c.status).length}
              </p>
              <p className="text-sm text-gray-500">{t.stat_active_codes}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
              <p className="text-2xl font-bold text-blue-600">{totalUsed}</p>
              <p className="text-sm text-gray-500">{t.stat_uses}</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 text-center">
              <p className={`text-2xl font-bold ${remaining === 0 ? 'text-red-500' : 'text-orange-500'}`}>
                {remaining !== null ? remaining : '∞'}
              </p>
              <p className="text-sm text-gray-500">{t.stat_remaining}</p>
              {remaining !== null && (
                <p className="text-xs text-gray-400 mt-0.5">{totalUsed} / {totalMax}</p>
              )}
            </div>
          </div>
        );
      })()}

      {/* Search */}
      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder={t.search_code}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="py-16 text-center text-gray-400">
            <KeyRound size={32} className="mx-auto mb-3 text-gray-300" />
            <p>{t.no_codes}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <Th col="code"        label={t.col_code}        {...thProps} className="px-4 py-3 text-left font-semibold text-gray-600" />
                <Th col="company"     label={t.col_company}     {...thProps} className="px-4 py-3 text-left font-semibold text-gray-600" />
                <Th col="status"      label={t.col_status_click} {...thProps} className="px-4 py-3 text-center font-semibold text-gray-600" />
                <th className="px-4 py-3 text-center font-semibold text-gray-600">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => toggle('used')} className="flex items-center gap-0.5 hover:text-blue-600 transition">
                      {t.col_used_max.split('/')[0].trim()}
                      <SortIcon col="used" sortKey={sortKey} sortDir={sortDir} />
                    </button>
                    <span className="text-gray-400">/</span>
                    <button onClick={() => toggle('max')} className="flex items-center gap-0.5 hover:text-blue-600 transition">
                      Max
                      <SortIcon col="max" sortKey={sortKey} sortDir={sortDir} />
                    </button>
                    <span className="text-gray-400 font-normal text-xs ml-1">{t.col_used_max_click}</span>
                  </div>
                </th>
                <Th col="registrants" label={t.col_registrants} {...thProps} className="px-4 py-3 text-right font-semibold text-gray-600" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const company = getCompany(c);
                const isFull = c.max_participants > 0 && c.used_count >= c.max_participants;
                return (
                  <React.Fragment key={c.id}>
                    <tr
                      className="border-b border-gray-50 hover:bg-gray-50 transition cursor-pointer"
                      onClick={() => c.users?.length > 0 && setExpanded(expanded === c.id ? null : c.id)}
                    >
                      {/* Code */}
                      <td className="px-4 py-3">
                        <span className="font-mono font-bold text-blue-700 tracking-wider">{c.code}</span>
                      </td>

                      {/* Entreprise — déduite des utilisateurs */}
                      <td className="px-4 py-3 text-gray-700">
                        {company
                          ? <span>{company}</span>
                          : <span className="text-gray-400 text-xs italic">{t.no_registrant}</span>
                        }
                      </td>

                      {/* Statut — toggle cliquable */}
                      <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                        {savingStatus === c.id ? (
                          <div className="flex justify-center">
                            <div className="animate-spin h-4 w-4 border-2 border-gray-400 border-t-transparent rounded-full" />
                          </div>
                        ) : (
                          <button
                            onClick={e => toggleStatus(e, c)}
                            title={c.status === 'Active' ? t.tip_disable : t.tip_enable}
                            className="group"
                          >
                            {c.status === 'Active' ? (
                              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-green-100 text-green-700 group-hover:bg-red-50 group-hover:text-red-500 transition-colors cursor-pointer">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 group-hover:bg-red-400" />
                                {t.status_active}
                              </span>
                            ) : c.status === 'Disabled' ? (
                              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-gray-100 text-gray-500 group-hover:bg-green-50 group-hover:text-green-600 transition-colors cursor-pointer">
                                <span className="w-1.5 h-1.5 rounded-full bg-gray-400 group-hover:bg-green-500" />
                                {t.status_disabled}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium bg-yellow-50 text-yellow-600 group-hover:bg-green-50 group-hover:text-green-600 transition-colors cursor-pointer">
                                <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 group-hover:bg-green-500" />
                                {t.status_new}
                              </span>
                            )}
                          </button>
                        )}
                      </td>

                      {/* Utilisé / Max — éditable */}
                      <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1.5">
                          <span className={`font-semibold ${isFull ? 'text-red-600' : 'text-gray-800'}`}>
                            {c.used_count}
                          </span>
                          <span className="text-gray-400">/</span>
                          {editingMax === c.id ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                min="0"
                                value={editMaxVal}
                                onChange={e => setEditMaxVal(e.target.value)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') saveMax(c.id);
                                  if (e.key === 'Escape') setEditingMax(null);
                                }}
                                onBlur={() => saveMax(c.id)}
                                autoFocus
                                className="w-14 text-center border border-blue-400 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              {savingMax === c.id && (
                                <div className="animate-spin h-3 w-3 border border-blue-500 border-t-transparent rounded-full" />
                              )}
                            </div>
                          ) : (
                            <button
                              onClick={e => startEditMax(e, c)}
                              className="font-medium text-gray-600 hover:text-blue-600 hover:underline underline-offset-2 transition"
                              title="Cliquer pour modifier"
                            >
                              {c.max_participants > 0 ? c.max_participants : '∞'}
                            </button>
                          )}
                        </div>
                      </td>

                      {/* Inscrits */}
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1 text-gray-500">
                          <Users size={13} />
                          <span className="font-medium">{c.users?.length || 0}</span>
                          {c.users?.length > 0 && (
                            expanded === c.id
                              ? <ChevronUp size={13} className="ml-0.5 text-blue-500" />
                              : <ChevronDown size={13} className="ml-0.5" />
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Ligne déroulante — liste des inscrits */}
                    {expanded === c.id && c.users?.length > 0 && (
                      <tr className="bg-blue-50 border-b border-blue-100">
                        <td colSpan={5} className="px-6 py-3">
                          <p className="text-xs font-semibold text-blue-600 mb-2 uppercase tracking-wide">
                            {t.registrants_title}
                          </p>
                          <div className="space-y-1.5">
                            {c.users.map((u, idx) => (
                              <div key={idx} className="flex items-center gap-3 text-sm text-gray-700">
                                <UserCheck size={14} className="text-blue-400 flex-shrink-0" />
                                <span className="font-medium">{u.name || '—'}</span>
                                {u.company && (
                                  <span className="text-gray-400 text-xs bg-white px-2 py-0.5 rounded-full border border-gray-200">
                                    {u.company}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// ─── Main OrganizerDashboard ──────────────────────────────────────────────────

const OrganizerDashboard = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { organizer, logout, canAccessSubEvent, orgHeaders } = useOrganizerAuth();
  const { event, loadEventBySlug } = useEvent();

  const subEventId = event?.subevent_id || event?.id || null;

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [lang, setLang] = useState(getOrgLang);
  const t = ORG_I18N[lang] || ORG_I18N.fr;

  const handleLangChange = (l) => {
    setLang(l);
    localStorage.setItem('org_language', l);
  };

  // Data
  const [participants, setParticipants] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [requests, setRequests] = useState([]);
  const [slots, setSlots] = useState([]);
  const [codes, setCodes] = useState([]);
  const [registrationUsers, setRegistrationUsers] = useState([]);
  const [companyTypes, setCompanyTypes] = useState([]);
  const [networkingParticipants, setNetworkingParticipants] = useState([]);
  const [networkingConnections, setNetworkingConnections] = useState([]);

  // ── Charger l'événement par slug si pas encore en contexte ──
  useEffect(() => {
    if (slug && (!event || event.slug !== slug)) {
      loadEventBySlug(slug);
    }
  }, [slug]); // eslint-disable-line

  const loadData = useCallback(async () => {
    if (!subEventId) return;
    setLoading(true);
    try {
      const safeFetch = async (url, extraHeaders = {}) => {
        try {
          const res = await fetch(url, { headers: extraHeaders });
          const json = await res.json();
          if (!res.ok) return null;
          return json;
        } catch (e) { return null; }
      };

      const [participantsData, meetingsData, requestsData, slotsData, codesData, regUsersData, typesData, netPartData, netConnData] = await Promise.all([
        safeFetch(`${API}/participants/${subEventId}`, orgHeaders()),
        safeFetch(`${API}/all-meetings/${subEventId}`, orgHeaders()),
        safeFetch(`${API}/all-requests/${subEventId}`, orgHeaders()),
        safeFetch(`${API}/slots-all/${subEventId}`),
        safeFetch(`${API}/registration-codes/${subEventId}`, orgHeaders()),
        safeFetch(`${API}/registration-users/${subEventId}`, orgHeaders()),
        safeFetch(`${API}/company-types?subevent_id=${encodeURIComponent(subEventId)}`),
        safeFetch(`${API}/networking-participants/${subEventId}`),
        safeFetch(`${API}/all-networking/${subEventId}`, orgHeaders()),
      ]);

      if (participantsData?.success) setParticipants(participantsData.data || []);
      if (meetingsData?.success)     setMeetings(meetingsData.data || []);
      if (requestsData?.success)     setRequests(requestsData.data || []);
      if (slotsData?.success)        setSlots(slotsData.data || []);
      if (codesData?.success)        setCodes(codesData.data || []);
      if (regUsersData?.success)     setRegistrationUsers(regUsersData.data || []);
      if (typesData?.success)        setCompanyTypes(typesData.data || []);
      if (netPartData?.success)      setNetworkingParticipants(netPartData.data || []);
      if (netConnData?.success)      setNetworkingConnections(netConnData.data || []);

      setLastRefresh(new Date());
    } finally {
      setLoading(false);
    }
  }, [subEventId]);

  useEffect(() => {
    if (!organizer) { navigate(slug ? `/event/${slug}/admin/login` : '/admin/login'); return; }
    if (subEventId && !canAccessSubEvent(subEventId)) {
      navigate('/');
      return;
    }
    loadData();
  }, [organizer, subEventId]); // eslint-disable-line

  // ── Stats ──
  const acceptedReg   = registrationUsers.filter(u => u.registration === 'Accepted').length;
  const confirmedMtgs = meetings.filter(m => m.status === 'Confirmed').length;
  const stats = {
    totalParticipants:   participants.length,
    totalCompanies:      [...new Set(participants.map(p => p.company).filter(Boolean))].length,
    totalRequests:       requests.length,
    pendingRequests:     requests.filter(r => r.status === 'Pending').length,
    confirmedMeetings:   confirmedMtgs,
    completeProfiles:    participants.filter(p => p.photo && p.bio && p.job_title).length,
    profileCompletionRate: participants.length
      ? Math.round(participants.filter(p => p.photo && p.bio && p.job_title).length / participants.length * 100)
      : 0,
    totalSlotCapacity:   slots.length * Math.max(1, [...new Set(meetings.map(m => m.table_label).filter(Boolean))].length),
    slotOccupationRate:  slots.length
      ? Math.round(confirmedMtgs / Math.max(slots.length, 1) * 100)
      : 0,
    // Inscriptions
    totalRegistrations:    registrationUsers.length,
    acceptedRegistrations: acceptedReg,
    newRegistrations:      registrationUsers.filter(u => u.registration === 'New').length,
    pendingRegistrations:  registrationUsers.filter(u => u.registration === 'Pending').length,
    declinedRegistrations: registrationUsers.filter(u => u.registration === 'Declined').length,
    acceptanceRate:        registrationUsers.length ? Math.round(acceptedReg / registrationUsers.length * 100) : 0,
    // Codes
    freeCodesCount: codes.filter(c => c.status === 'Free').length,
    // Check-in
    checkedIn: participants.filter(p => p.checkin_status === 'Checked In').length,
    // Meetings / participant
    avgMeetingsPerParticipant: participants.length
      ? (confirmedMtgs * 2 / participants.length).toFixed(1)
      : '0',
    // Top 5 pays
    topCountries: Object.entries(
      participants.reduce((acc, p) => { if (p.country) acc[p.country] = (acc[p.country] || 0) + 1; return acc; }, {})
    ).sort((a, b) => b[1] - a[1]).slice(0, 5),
    // Matchmaking request breakdown
    acceptedRequests:   requests.filter(r => r.status === 'Accepted').length,
    rejectedRequests:   requests.filter(r => r.status === 'Rejected').length,
    cancelledRequests:  requests.filter(r => r.status === 'Cancelled').length,
    // Categories / Enjeux
    topCategories: Object.entries(
      participants.flatMap(p => p.category || []).reduce((acc, c) => { if (c) acc[c] = (acc[c] || 0) + 1; return acc; }, {})
    ).sort((a, b) => b[1] - a[1]).slice(0, 8),
    // Demandes / Offres (company_requests)
    topRequests: Object.entries(
      participants.flatMap(p => p.company_requests || []).reduce((acc, r) => { if (r) acc[r] = (acc[r] || 0) + 1; return acc; }, {})
    ).sort((a, b) => b[1] - a[1]).slice(0, 8),
    // Networking
    networkingUsers:    networkingParticipants.length,
    netConnected:       networkingConnections.filter(c => c.status === 'on').length,
    netPending:         networkingConnections.filter(c => c.status === 'Pending').length,
    netDeclined:        networkingConnections.filter(c => c.status === 'decline').length,
    netTotal:           networkingConnections.length,
  };

  const tabs = [
    { key: 'overview',     label: t.tab_overview,      icon: BarChart2,      badge: null },
    { key: 'participants', label: t.tab_participants,   icon: Users,          badge: participants.length },
    { key: 'agenda',       label: t.tab_agenda,         icon: Calendar,       badge: meetings.filter(m => m.status === 'Confirmed').length },
    { key: 'tables',       label: t.tab_tables,         icon: Table2,         badge: null },
    { key: 'requests',     label: t.tab_requests,       icon: MessageSquare,  badge: requests.filter(r => r.status === 'Pending').length || null },
    { key: 'codes',        label: t.tab_codes,          icon: KeyRound,       badge: codes.length || null },
    { key: 'content',      label: t.tab_content,        icon: LayoutGrid,     badge: null },
  ];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Header ── */}
      <header className="bg-white border-b border-gray-100 shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img
              src="https://matchmaking.mobivent.fr/logo-2025-Mobivent-400.png"
              alt="Mobivent"
              className="h-8"
              onError={(e) => e.target.style.display = 'none'}
            />
            <div>
              <p className="text-sm font-bold text-gray-900">{event?.name || slug || 'Organizer'}</p>
              <p className="text-xs text-gray-400 capitalize">{organizer?.role} · {organizer?.name}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {lastRefresh && (
              <span className="hidden sm:block text-xs text-gray-400">
                {t.updated} {lastRefresh.toLocaleTimeString()}
              </span>
            )}
            {/* Sélecteur de langue */}
            <div className="relative">
              <select
                value={lang}
                onChange={e => handleLangChange(e.target.value)}
                className="appearance-none bg-gray-50 border border-gray-200 rounded-lg pl-3 pr-7 py-1.5 text-xs text-gray-700 focus:ring-2 focus:ring-blue-500 cursor-pointer"
                title={t.lang_label}
              >
                {SUPPORTED_LANGS.map(l => (
                  <option key={l} value={l}>{LANG_LABELS[l]}</option>
                ))}
              </select>
              <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
            <button
              onClick={loadData}
              className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 transition"
              title={t.refresh}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => { logout(); navigate(slug ? `/event/${slug}/admin/login` : '/admin/login'); }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold text-gray-500 hover:bg-gray-100 transition"
            >
              <LogOut size={14} /> {t.logout}
            </button>
          </div>
        </div>

        {/* Nav tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {tabs.map(({ key, label, icon: Icon, badge }) => (
              <button
                key={key}
                onClick={() => { setActiveTab(key); loadData(); }}
                className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-3 font-semibold text-xs sm:text-sm transition-all whitespace-nowrap border-b-2 ${
                  activeTab === key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-200'
                }`}
              >
                <Icon size={15} />
                {label}
                {badge !== null && badge > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                    activeTab === key ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
                  }`}>{badge}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* ── Content ── */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            {activeTab === 'overview'     && <OverviewTab stats={stats} companyTypes={companyTypes} t={t} />}
            {activeTab === 'participants' && (
              <ParticipantsWithSubTabs
                participants={participants}
                registrationUsers={registrationUsers}
                setRegistrationUsers={setRegistrationUsers}
                orgHeaders={orgHeaders}
                t={t}
                meetings={meetings}
                slots={slots}
                eventName={event?.name || ''}
                subEventId={subEventId}
                eventSlug={slug}
              />
            )}
            {activeTab === 'agenda' && (
              <AgendaTab
                meetings={meetings}
                slots={slots}
                participants={participants}
                t={t}
                orgHeaders={orgHeaders}
                onMeetingUpdate={(id, updates) => setMeetings(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m))}
              />
            )}
            {activeTab === 'tables'       && <TablesTab meetings={meetings} slots={slots} t={t} />}
            {activeTab === 'requests'     && <RequestsTab requests={requests} t={t} />}
            {activeTab === 'codes'        && <CodesTab codes={codes} orgHeaders={orgHeaders} t={t} />}
            {activeTab === 'content'      && <ContentManagementTab subEventId={subEventId} orgHeaders={orgHeaders} t={t} />}
          </>
        )}
      </main>
    </div>
  );
};

export default OrganizerDashboard;
